// Component Name: ShippingBanner
// Data is retrieved from Contentful
// The ShippingBanner component showcases banners that automatically rotate every 5 seconds, keeping users updated with the latest offers.
// Designed for responsiveness, the component adjusts its layout seamlessly across various device sizes.

import React, { useState, useEffect } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import { Banner } from 'types/next';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import useLocale from 'helpers/hooks/useLocale';
import useMobileDevice from 'helpers/hooks/useMobileDevice';

interface ShippingBannerProps {
  shippingBannerContent?: Banner[];
}

const ShippingBanner = ({ shippingBannerContent }: ShippingBannerProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { formatMessage } = useFormat({ name: 'common' });
  const { getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';
  const { isMobileDevice } = useMobileDevice();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (shippingBannerContent ? (prevIndex + 1) % shippingBannerContent?.length : 0));
    }, 5000);

    return () => clearInterval(interval);
  }, [shippingBannerContent]);

  if (!shippingBannerContent || shippingBannerContent?.length === 0) {
    return <div>{formatMessage({ id: 'shippingBanner', defaultMessage: 'No banner content available' })}</div>;
  }

  const currentBanner = shippingBannerContent?.[currentIndex];
  return (
    <>
      {currentBanner?.bannertext?.[selectedLocale] && (
        <div className="flex min-h-[70px]  w-full items-center justify-center p-[12.8px]">
          <SmartLink
            href={`${currentBanner?.link?.[selectedLocale] || ''}`}
            className="flex items-center justify-center"
          >
            <div
              className={`flex items-center justify-center text-center leading-[100%] transition-all duration-1000 ease-in-out`}
            >
              <p
                style={{
                  fontSize: isMobileDevice
                    ? currentBanner?.bannertext?.fontSizeMobilePx ?? '20px'
                    : currentBanner?.bannertext?.fontSizeDesktopPx ?? '16px',
                  color: currentBanner?.bannertext?.textColor ?? '#000000',
                  fontWeight: currentBanner?.bannertext?.fontWeight ?? '700',
                }}
              >
                {currentBanner?.bannertext?.[selectedLocale]}
              </p>
            </div>
            {currentBanner?.image?.[selectedLocale] && (
              <img
                src={`https://${currentBanner?.image?.[selectedLocale]?.defaultHost}/i/${currentBanner?.image?.[selectedLocale]?.endpoint}/${currentBanner?.image?.[selectedLocale]?.name}`}
                alt={currentBanner?.name?.[selectedLocale]}
                style={{
                  maxWidth: isMobileDevice
                    ? currentBanner?.image?.width?.mobile ?? '70px'
                    : currentBanner?.image?.width?.desktop ?? '70px',
                }}
                className={`ml-6 h-auto transition-all duration-1000 ease-in-out`}
              />
            )}
          </SmartLink>
        </div>
      )}
    </>
  );
};

export default ShippingBanner;
